import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { useNavigate } from 'react-router-dom';
import { 
  Users, BarChart3, Shield, UserCog, Settings, Flag, TrendingUp,
  Briefcase, Home, Heart, Trophy, GraduationCap, Building, Gift, Check, X, Bell
} from 'lucide-react';
import { toast } from 'sonner';
import { ROLE_LABELS } from '../../lib/utils';

export default function AdminDashboard() {
  const { axiosAuth } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [execMetrics, setExecMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [flags, setFlags] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const api = axiosAuth();

  useEffect(() => {
    Promise.all([
      api.get('/analytics/overview').catch(() => ({ data: null })),
      api.get('/analytics/executive').catch(() => ({ data: null })),
      api.get('/users').catch(() => ({ data: [] })),
      api.get('/flags').catch(() => ({ data: [] })),
      api.get('/admin/pending-users').catch(() => ({ data: [] }))
    ]).then(([analyticsRes, execRes, usersRes, flagsRes, pendingRes]) => {
      setAnalytics(analyticsRes.data);
      setExecMetrics(execRes.data);
      setUsers(usersRes.data || []);
      setFlags(flagsRes.data || []);
      setPendingUsers(pendingRes.data || []);
      setLoading(false);
    });
  }, []);

  const byRole = {};
  users.forEach(u => { byRole[u.role] = (byRole[u.role] || 0) + 1; });
  const activeFlags = flags.filter(f => !f.resolved);

  const handleUserApproval = async (userId, action) => {
    try {
      await api.put(`/admin/approve-user/${userId}?action=${action}`);
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      toast.success(`User ${action}d`);
    } catch { toast.error('Failed to process'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-teal-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold" style={{ fontFamily: 'Manrope' }}>Executive Dashboard</h1>
        <p className="text-muted-foreground mt-1 text-sm">Full platform oversight and outcome tracking</p>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card 
          className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow" 
          onClick={() => navigate('/clients?status=active')}
          data-testid="stat-participants"
        >
          <CardContent className="p-4">
            <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center mb-2"><Users className="w-4 h-4 text-teal-600" /></div>
            <p className="text-2xl font-bold">{execMetrics?.overview?.total_participants || 0}</p>
            <p className="text-xs text-muted-foreground">Active Participants</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/clients?activity=30d')}
          data-testid="stat-active"
        >
          <CardContent className="p-4">
            <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center mb-2"><TrendingUp className="w-4 h-4 text-sky-600" /></div>
            <p className="text-2xl font-bold">{execMetrics?.overview?.active_users_30d || 0}</p>
            <p className="text-xs text-muted-foreground">Active (30 days)</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/users?group=staff')}
          data-testid="stat-staff"
        >
          <CardContent className="p-4">
            <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center mb-2"><UserCog className="w-4 h-4 text-amber-600" /></div>
            <p className="text-2xl font-bold">{execMetrics?.overview?.total_staff || 0}</p>
            <p className="text-xs text-muted-foreground">Staff Members</p>
          </CardContent>
        </Card>
        <Card 
          className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
          onClick={() => navigate('/flags?status=active')}
          data-testid="stat-flags"
        >
          <CardContent className="p-4">
            <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center mb-2"><Flag className="w-4 h-4 text-red-500" /></div>
            <p className="text-2xl font-bold">{activeFlags.length}</p>
            <p className="text-xs text-muted-foreground">Active Flags</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending User Approvals */}
      {pendingUsers.length > 0 && (
        <Card className="border-0 shadow-sm rounded-2xl border-l-4 border-l-amber-400" data-testid="pending-users-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Bell className="w-4 h-4 text-amber-600" /> Pending User Approvals ({pendingUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u.id} className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 border border-amber-200" data-testid={`pending-user-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-sm font-semibold text-amber-700">{u.full_name?.charAt(0)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{u.full_name}</p>
                  <p className="text-xs text-muted-foreground">{u.email} &middot; {ROLE_LABELS?.[u.role] || u.role}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" className="h-7 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1" onClick={() => handleUserApproval(u.id, 'approve')} data-testid={`approve-user-${u.id}`}>
                    <Check size={12} /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 rounded-full text-xs text-red-600 gap-1" onClick={() => handleUserApproval(u.id, 'reject')} data-testid={`reject-user-${u.id}`}>
                    <X size={12} /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Outcome Metrics */}
      {execMetrics?.outcome_metrics && (
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <TrendingUp className="w-4 h-4 text-teal-600" />
              Outcome Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div 
                className="p-4 rounded-xl bg-sky-50 cursor-pointer hover:bg-sky-100 transition-colors"
                onClick={() => navigate('/employment')}
                data-testid="admin-outcome-employment"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Briefcase className="w-5 h-5 text-sky-600" />
                  <span className="text-sm font-medium text-sky-800">Employment</span>
                </div>
                <p className="text-3xl font-bold text-sky-700">{execMetrics.outcome_metrics.employment_rate}%</p>
                <p className="text-xs text-sky-600">{execMetrics.outcome_metrics.employment_count} participants employed</p>
                <Progress value={execMetrics.outcome_metrics.employment_rate} className="h-2 mt-2 bg-sky-100" />
              </div>
              <div 
                className="p-4 rounded-xl bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors"
                onClick={() => navigate('/housing')}
                data-testid="admin-outcome-housing"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Home className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800">Housing</span>
                </div>
                <p className="text-3xl font-bold text-emerald-700">{execMetrics.outcome_metrics.housing_stability_rate}%</p>
                <p className="text-xs text-emerald-600">{execMetrics.outcome_metrics.housing_secured_count} stable housing</p>
                <Progress value={execMetrics.outcome_metrics.housing_stability_rate} className="h-2 mt-2 bg-emerald-100" />
              </div>
              <div 
                className="p-4 rounded-xl bg-violet-50 cursor-pointer hover:bg-violet-100 transition-colors"
                onClick={() => navigate('/analytics')}
                data-testid="admin-outcome-sobriety"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-5 h-5 text-violet-600" />
                  <span className="text-sm font-medium text-violet-800">Sobriety</span>
                </div>
                <p className="text-3xl font-bold text-violet-700">{execMetrics.outcome_metrics.sobriety_engagement_rate}%</p>
                <p className="text-xs text-violet-600">reporting sobriety</p>
                <Progress value={execMetrics.outcome_metrics.sobriety_engagement_rate} className="h-2 mt-2 bg-violet-100" />
              </div>
              <div 
                className="p-4 rounded-xl bg-amber-50 cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate('/analytics')}
                data-testid="admin-outcome-engagement"
              >
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-amber-600" />
                  <span className="text-sm font-medium text-amber-800">Engagement</span>
                </div>
                <p className="text-3xl font-bold text-amber-700">{execMetrics.outcome_metrics.program_engagement_rate}%</p>
                <p className="text-xs text-amber-600">active in last 30 days</p>
                <Progress value={execMetrics.outcome_metrics.program_engagement_rate} className="h-2 mt-2 bg-amber-100" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-3 gap-4">
        {/* Program Activity */}
        {execMetrics?.program_activity && (
          <Card className="border-0 shadow-sm rounded-2xl">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Program Activity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div 
                  className="flex justify-between items-center p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate('/checkins')}
                  data-testid="admin-activity-checkins"
                >
                  <span className="text-sm text-muted-foreground">Total Check-ins</span>
                  <span className="text-sm font-bold">{execMetrics.program_activity.total_checkins}</span>
                </div>
                <div 
                  className="flex justify-between items-center p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate('/group-sessions')}
                  data-testid="admin-activity-peer-sessions"
                >
                  <span className="text-sm text-muted-foreground">Peer Sessions</span>
                  <span className="text-sm font-bold">{execMetrics.program_activity.peer_sessions}</span>
                </div>
                <div 
                  className="flex justify-between items-center p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate('/referrals')}
                  data-testid="admin-activity-referrals-made"
                >
                  <span className="text-sm text-muted-foreground">Referrals Made</span>
                  <span className="text-sm font-bold">{execMetrics.program_activity.referrals_made}</span>
                </div>
                <div 
                  className="flex justify-between items-center p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate('/referrals?status=completed')}
                  data-testid="admin-activity-referrals-completed"
                >
                  <span className="text-sm text-muted-foreground">Referrals Completed</span>
                  <span className="text-sm font-bold text-emerald-600">{execMetrics.program_activity.referrals_completed}</span>
                </div>
                <div 
                  className="flex justify-between items-center p-2 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => navigate('/courses')}
                  data-testid="admin-activity-course-lessons"
                >
                  <span className="text-sm text-muted-foreground">Course Lessons</span>
                  <span className="text-sm font-bold">{execMetrics.program_activity.courses_lessons_completed}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Journey Milestones */}
        {execMetrics?.journey_milestones && (
          <Card 
            className="border-0 shadow-sm rounded-2xl cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/analytics')}
            data-testid="admin-journey-progress"
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Trophy className="w-4 h-4 text-amber-500" />
                Journey Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-4">
                <p className="text-3xl font-bold text-amber-600">{execMetrics.journey_milestones.total_achieved}</p>
                <p className="text-xs text-muted-foreground">Total Life Milestones Achieved</p>
              </div>
              <div className="space-y-2">
                {Object.entries(execMetrics.journey_milestones.by_category || {}).slice(0, 5).map(([cat, count]) => (
                  <div key={cat} className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground capitalize">{cat.replace('_', ' ')}</span>
                    <Badge variant="secondary">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users by Role */}
        <Card className="border-0 shadow-sm rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Users by Role</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2.5">
              {Object.entries(byRole).map(([role, count]) => (
                <div 
                  key={role} 
                  className="flex justify-between items-center cursor-pointer hover:bg-muted/30 p-1.5 rounded-lg transition-colors"
                  onClick={() => navigate(`/users?role=${role}`)}
                  data-testid={`admin-role-${role}`}
                >
                  <span className="text-sm text-muted-foreground capitalize">{role.replace('_', ' ')}</span>
                  <Badge variant="secondary" className="bg-muted">{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="border-0 shadow-sm rounded-2xl">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold" style={{ fontFamily: 'Manrope' }}>Administration</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-teal-50" onClick={() => navigate('/users')} data-testid="admin-user-mgmt">
            <UserCog size={18} className="text-teal-600" /><span className="text-xs">User Management</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-sky-50" onClick={() => navigate('/analytics')} data-testid="admin-analytics">
            <BarChart3 size={18} className="text-sky-600" /><span className="text-xs">Analytics</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-amber-50" onClick={() => navigate('/compliance')} data-testid="admin-compliance">
            <Shield size={18} className="text-amber-600" /><span className="text-xs">Compliance</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1.5 rounded-xl hover:bg-violet-50" onClick={() => navigate('/opportunities')} data-testid="admin-opportunities">
            <Gift size={18} className="text-violet-600" /><span className="text-xs">Opportunities</span>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
